<!-- eslint-disable vue/no-bare-strings-in-template, @intlify/vue-i18n/no-raw-text -->
<script setup>
// Lateral "Histórico de conversa" panel extracted from HistoricoCliente.vue.
// Pure presentation: receives the conversation list, selection, messages and
// load state via props, emits selection/retry up. DOM is byte-identical to the
// original inline <aside>.
import LeadHistoryState from './LeadHistoryState.vue';

const props = defineProps({
  conversations: { type: Array, default: () => [] },
  selectedConversationId: {
    type: [Number, String],
    default: null,
  },
  messages: { type: Array, default: () => [] },
  isLoadingMessages: { type: Boolean, default: false },
  conversationLoadError: { type: Boolean, default: false },
  conversationErrorCopy: {
    type: Object,
    default: () => ({ title: '', description: '' }),
  },
  leadName: { type: String, default: '' },
});

defineEmits(['select', 'retry']);

const toDate = value => {
  if (!value) return null;

  const numeric = Number(value);
  if (Number.isFinite(numeric)) {
    return new Date(numeric < 1_000_000_000_000 ? numeric * 1000 : numeric);
  }

  const parsed = new Date(value);
  return Number.isNaN(parsed.getTime()) ? null : parsed;
};

const formatDateTime = value => {
  const date = toDate(value);
  return date ? date.toLocaleString('pt-BR') : '—';
};

const previewConversation = conversation =>
  conversation.last_non_activity_message?.content?.trim() ||
  conversation.messages?.[0]?.content?.trim() ||
  'Sem mensagens ainda';

const attachmentUrl = attachment =>
  attachment.data_url ||
  attachment.file_url ||
  attachment.thumb_url ||
  attachment.external_url ||
  '';

const availableAttachments = message =>
  (message.attachments || []).filter(attachment => attachmentUrl(attachment));

const messageDirection = message => {
  if (Number(message.message_type) === 0) return 'incoming';
  if (Number(message.message_type) === 1) return 'outgoing';
  return 'system';
};

const messageSender = message => {
  if (message.sender?.name) return message.sender.name;
  return messageDirection(message) === 'incoming'
    ? props.leadName || 'Cliente'
    : 'Lais';
};

const getMessageText = message => {
  const content = message.content?.trim();
  if (content) return content;

  const transcript =
    message.content_attributes?.transcribed_text ||
    message.content_attributes?.transcription ||
    message.content_attributes?.audio_transcript;
  if (transcript) {
    return `Transcrição de áudio\n${transcript}`;
  }

  const attachments = availableAttachments(message);
  if (attachments.length) {
    return attachments.length === 1
      ? '1 anexo enviado'
      : `${attachments.length} anexos enviados`;
  }

  if (message.attachments?.length) return 'Anexo indisponível';

  return 'Mensagem sem texto';
};
</script>

<template>
  <!-- eslint-disable vue/no-bare-strings-in-template, @intlify/vue-i18n/no-raw-text -->
  <aside
    class="flex min-h-[28rem] flex-col border-t border-[#e5e7eb] bg-white lg:min-h-0 lg:border-l lg:border-t-0"
  >
    <header class="px-6 py-5 border-b border-[#e5e7eb]">
      <h3 class="text-[16px] font-semibold text-lais-text font-redHatDisplay">
        Histórico de conversa
      </h3>
      <p class="text-[12px] text-lais-text-muted mt-0.5">
        Entenda o contexto para prover um atendimento contínuo.
      </p>
    </header>
    <div
      v-if="conversations.length"
      class="overflow-x-auto overscroll-x-contain border-b border-[#e5e7eb] bg-[#fafafa] px-4 py-4 sm:px-6 lg:max-h-60 lg:overflow-x-hidden lg:overflow-y-auto lg:overscroll-contain"
    >
      <div class="flex min-w-max gap-2 lg:min-w-0 lg:flex-col">
        <button
          v-for="conv in conversations"
          :key="conv.id"
          type="button"
          :aria-label="`Abrir conversa #${conv.id}`"
          :aria-pressed="String(conv.id) === String(selectedConversationId)"
          class="min-w-[min(18rem,calc(100vw-3rem))] rounded-lais border p-3 text-left transition-colors focus-visible:outline-none focus-visible:shadow-lais-focus lg:min-w-0"
          :class="
            String(conv.id) === String(selectedConversationId)
              ? 'border-lais-primary bg-white shadow-lais-card'
              : 'border-[#e5e7eb] bg-white hover:border-[#d7c8ef]'
          "
          @click="$emit('select', conv.id)"
        >
          <div class="flex items-center justify-between gap-3">
            <p class="text-[12px] font-semibold text-lais-text">
              Conversa #{{ conv.id }}
            </p>
            <span class="text-[11px] uppercase text-lais-text-muted">
              {{ conv.status }}
            </span>
          </div>
          <p class="text-[11px] text-lais-text-muted mt-1">
            {{ formatDateTime(conv.last_activity_at || conv.created_at) }}
          </p>
          <p class="text-[12px] text-lais-text-body mt-1 truncate">
            {{ previewConversation(conv) }}
          </p>
        </button>
      </div>
    </div>
    <div class="flex-1 overflow-auto overscroll-contain px-6 py-4">
      <div
        v-if="isLoadingMessages"
        class="text-center mt-12 text-[14px] font-semibold text-lais-text-muted"
      >
        Carregando mensagens…
      </div>
      <LeadHistoryState
        v-else-if="conversationLoadError"
        container-class="mt-12 rounded-lais-sm border border-dashed border-[#eee5d6] bg-[#fafafa] p-5 text-center"
        :title="conversationErrorCopy.title"
        :description="conversationErrorCopy.description"
        action-label="Tentar novamente"
        @action="$emit('retry')"
      />
      <p
        v-else-if="conversations.length === 0 || messages.length === 0"
        class="text-center mt-12 text-[14px] font-semibold text-lais-primary"
      >
        Não há mensagens para esta conversa
      </p>
      <ul v-else class="flex flex-col gap-3">
        <li v-for="message in messages" :key="message.id">
          <div
            v-if="messageDirection(message) === 'system'"
            class="px-3 py-2 rounded-full bg-[#f5f5f5] text-[12px] text-lais-text-body text-center"
          >
            {{ getMessageText(message) }}
          </div>
          <div
            v-else
            class="flex"
            :class="
              messageDirection(message) === 'outgoing'
                ? 'justify-end'
                : 'justify-start'
            "
          >
            <article
              class="max-w-[88%] rounded-2xl border px-4 py-3 text-[13px] shadow-sm"
              :class="
                messageDirection(message) === 'outgoing'
                  ? 'bg-[#f4effd] border-[#d7c8ef] text-lais-text'
                  : 'bg-white border-[#e5e7eb] text-lais-text'
              "
            >
              <p class="text-[11px] font-semibold mb-1 text-lais-primary">
                {{ messageSender(message) }}
              </p>
              <p class="whitespace-pre-line break-words">
                {{ getMessageText(message) }}
              </p>
              <div
                v-if="availableAttachments(message).length"
                class="mt-2 flex flex-col gap-1"
              >
                <a
                  v-for="(attachment, index) in availableAttachments(message)"
                  :key="index"
                  :href="attachmentUrl(attachment)"
                  target="_blank"
                  rel="noopener noreferrer"
                  class="text-[12px] font-semibold text-lais-primary underline decoration-lais-warning decoration-[1.5px] underline-offset-[3px]"
                >
                  {{ attachment.file_type || 'Anexo' }}
                </a>
              </div>
              <p class="mt-2 text-[10px] text-lais-text-muted text-right">
                {{ formatDateTime(message.created_at) }}
              </p>
            </article>
          </div>
        </li>
      </ul>
    </div>
  </aside>
</template>
